import { Radio, RadioChangeEvent } from "antd";
import { clsx } from "clsx";
import { ReactNode, useState } from "react";
import { LabelBlock } from "../LabelBlock/LabelBlock";
import "./MultiSegmentControl.css";
import "./SegmentControl.css";

/**
 * Option type definition for MultiSegmentControl
 * @interface OptionType
 * @template T - Type parameter for the option value (must be a string type)
 */
export interface OptionType<T extends string> {
  /**
   * The display text for the option
   */
  label: string;

  /**
   * The value of the option
   */
  value: T;

  /**
   * Whether the option is disabled
   * @default false
   */
  disabled?: boolean;
}

/**
 * Text size options for disabled display mode
 */
export enum MultiSegmentTextSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

/**
 * Text alignment options for disabled display mode
 */
export enum MultiSegmentTextAlign {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}

/**
 * Props for MultiSegmentControl component
 * @interface MultiSegmentControlProps
 * @template T - Type parameter for the segment values (must be a string type)
 */
export interface MultiSegmentControlProps<T extends string> {
  /**
   * Size of the segment control
   * @default "default"
   */
  size?: "default" | "small";

  /**
   * Variant of the segment control
   * @default "filled"
   */
  variant?: "outline" | "filled";
  /**
   * Label text displayed above the control
   */
  label?: string;

  /**
   * Current selected value
   */
  value: T | null;

  /**
   * Available options for the segment control
   */
  options: OptionType<T>[];

  /**
   * Callback fired when value changes
   */
  onChange: (value: T) => void;

  /**
   * Optional callback fired when component gets focus
   */
  onFocus?: () => void;

  /**
   * Whether the control is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the control is in an error state
   * @default false
   */
  isError?: boolean;

  /**
   * Whether the control is in abnormal visual state
   * @default false
   */
  abnormal?: boolean;

  /**
   * Whether the control is visually highlighted (sky-blue) — e.g. to flag a
   * value that differs from its default. Shares the sky treatment with
   * `abnormal`; kept as a separate prop so the "changed" and "abnormal finding"
   * concepts stay independent across the physical-exam inputs.
   * @default false
   */
  highlighted?: boolean;

  /**
   * Description text displayed to the right of the content
   */
  description?: string;

  /**
   * Additional CSS class for the control
   */
  className?: string;

  /**
   * Additional CSS class for the container
   */
  containerClassName?: string;

  /**
   * Removes default padding when true
   * @default false
   */
  noPadding?: boolean;

  /**
   * Text size for disabled display mode
   * @default MultiSegmentTextSize.LARGE
   */
  textSize?: MultiSegmentTextSize;

  /**
   * Text alignment for disabled display mode
   * @default MultiSegmentTextAlign.LEFT
   */
  textAlign?: MultiSegmentTextAlign;

  /**
   * Custom text color for disabled display mode
   */
  textColor?: string;

  /**
   * Width of the block as a percentage
   * @default "100%"
   */
  widthPercent?: string;

  /**
   * Whether the control should stretch to fill its container width
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Content to render after the segment control
   */
  afterControl?: ReactNode;

  /**
   * Tooltip text shown on hover of a "?" icon next to the label (edit mode only).
   */
  infoTooltip?: string;

  /**
   * Additional props to be passed to the underlying elements
   */
  [key: string]: unknown;
}

/**
 * MultiSegmentControl Component
 *
 * A flexible segment control component that allows selection from multiple options.
 * Integrates with Indigo design system and supports error states and disabled modes.
 *
 * @template T - Type parameter for the segment values (must be a string type)
 *
 * @example
 * // Basic usage
 * <MultiSegmentControl
 *   label="Priority"
 *   value={priority}
 *   options={[
 *     { label: "Low", value: "low" },
 *     { label: "Medium", value: "medium" },
 *     { label: "High", value: "high" }
 *   ]}
 *   onChange={setPriority}
 * />
 *
 * @example
 * // With disabled options
 * <MultiSegmentControl
 *   label="Status"
 *   value={status}
 *   options={[
 *     { label: "Active", value: "active" },
 *     { label: "Pending", value: "pending" },
 *     { label: "Closed", value: "closed", disabled: true }
 *   ]}
 *   onChange={setStatus}
 * />
 *
 * @example
 * // Error state with description
 * <MultiSegmentControl
 *   label="Required Field"
 *   value={field}
 *   options={options}
 *   onChange={setField}
 *   isError={!field}
 *   description="This field is required"
 * />
 */
export function MultiSegmentControl<T extends string>({
  size = "default",
  variant = "filled",
  label,
  value,
  options,
  onChange,
  onFocus,
  disabled = false,
  isError = false,
  abnormal = false,
  highlighted = false,
  description,
  className,
  containerClassName,
  noPadding = false,
  textSize = MultiSegmentTextSize.LARGE,
  textAlign = MultiSegmentTextAlign.LEFT,
  textColor,
  widthPercent = "100%",
  fullWidth = false,
  afterControl,
  infoTooltip,
}: MultiSegmentControlProps<T>) {
  const [hasFocus, setHasFocus] = useState(false);

  /**
   * Handles radio change events
   * @param {RadioChangeEvent} e - The radio change event
   */
  const handleChange = (e: RadioChangeEvent) => {
    if (disabled) return;
    onChange(e.target.value as T);
  };

  /**
   * Handles focus events
   */
  const handleFocus = () => {
    setHasFocus(true);
    if (onFocus) onFocus();
  };

  /**
   * Gets the CSS class for text size based on the textSize prop
   * @returns {string} The CSS class for text size
   * Note: This function is kept for reference but no longer used with indigo-segment-disabled-text
   */
  const _getTextSizeClass = () => {
    switch (textSize) {
      case MultiSegmentTextSize.SMALL:
        return "text-sm";
      case MultiSegmentTextSize.MEDIUM:
        return "text-md";
      default:
        return "text-lg";
    }
  };

  /**
   * Gets the CSS class for text alignment based on the textAlign prop
   * @returns {string} The CSS class for text alignment
   */
  const getTextAlignClass = () => {
    switch (textAlign) {
      case MultiSegmentTextAlign.CENTER:
        return "text-center";
      case MultiSegmentTextAlign.RIGHT:
        return "text-right";
      default:
        return "text-left";
    }
  };

  // If disabled, render a simple text display
  if (disabled) {
    const currentDisplayLabel =
      options.find((option) => option.value === value)?.label ?? "-";

    return (
      <LabelBlock
        label={label}
        disabled={disabled}
        isError={isError}
        description={description}
        className={className}
        containerClassName={containerClassName}
        noPadding={noPadding}
        widthPercent={widthPercent}
        infoTooltip={infoTooltip}
      >
        <div
          className={`indigo-segment-disabled-text ${getTextAlignClass()}`}
          style={textColor ? { color: textColor } : undefined}
        >
          {currentDisplayLabel}
        </div>
        {afterControl}
      </LabelBlock>
    );
  }

  const renderSegmentControl = (
    <Radio.Group
      onChange={handleChange}
      onFocus={handleFocus}
      value={value || undefined}
      optionType="button"
      className={clsx(
        "indigo-segment-control indigo-multi-segment-control",
        size === "small" && "indigo-segment-control--small",
        variant === "filled" && "indigo-segment-control--filled",
        fullWidth && "indigo-segment-control--full-width",
        isError && "is-error",
        abnormal && "is-abnormal",
        highlighted && "is-highlighted",
        hasFocus && "is-focused",
        value && "has-value",
        className,
      )}
    >
      {options.map((option) => (
        <Radio.Button
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </Radio.Button>
      ))}
    </Radio.Group>
  );

  // If no label provided, return just the segment control
  if (!label) {
    return (
      <div className={className}>
        {renderSegmentControl}
        {afterControl}
      </div>
    );
  }

  // Return segment control with label block
  return (
    <LabelBlock
      label={label}
      disabled={disabled}
      isError={isError}
      description={description}
      className={className}
      containerClassName={containerClassName}
      noPadding={noPadding}
      widthPercent={widthPercent}
      infoTooltip={infoTooltip}
    >
      <div className="flex flex-row items-center gap-2">
        {renderSegmentControl}
        {afterControl}
      </div>
    </LabelBlock>
  );
}

export default MultiSegmentControl;
